import React, { useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileImage, 
  faCloudUploadAlt, 
  faTimes,
  faDownload,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";

export default function WebpToJpegConvert() {
  const { darkMode } = useTheme();
  const [images, setImages] = useState([]);
  const [quality, setQuality] = useState(85);
  const [isConverting, setIsConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  const convertImage = async (imageFile, quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(imageFile);

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, 'image/jpeg', quality / 100);

        URL.revokeObjectURL(img.src);
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Image loading failed: ' + error.type));
      };
    });
  };

  const handleFileUpload = (files) => {
    const webpFiles = Array.from(files).filter(
      (file) => file.type === "image/webp" || file.name.endsWith(".webp")
    );

    if (webpFiles.length === 0) return;

    const newImages = webpFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name.replace(/\.[^/.]+$/, ""),
      convertedUrl: null,
      converted: false,
    }));

    setImages((prev) => [...prev, ...newImages]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleInputChange = (e) => {
    handleFileUpload(e.target.files);
    e.target.value = ''; // Reset input to allow selecting same file again
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    if (newImages[index].convertedUrl) {
      URL.revokeObjectURL(newImages[index].convertedUrl);
    }
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const clearAll = () => {
    images.forEach((img) => {
      URL.revokeObjectURL(img.preview);
      if (img.convertedUrl) URL.revokeObjectURL(img.convertedUrl);
    });
    setImages([]);
  };

  const downloadImage = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadAll = () => {
    images.forEach((img) => {
      if (img.convertedUrl) {
        downloadImage(img.convertedUrl, `${img.name}.jpeg`);
      }
    });
  };

  const handleConvert = async () => {
    setIsConverting(true);
    const convertedImages = await Promise.all(
      images.map(async (img) => {
        if (!img.converted) {
          try {
            const convertedUrl = await convertImage(img.file, quality);
            return { ...img, convertedUrl, converted: true };
          } catch (error) {
            console.error('Conversion failed for', img.name, ':', error);
            return { ...img, convertedUrl: null, converted: false }; // Keep original if conversion fails
          }
        } else {
          return img;
        }
      })
    );
    setImages(convertedImages);
    setIsConverting(false);
  };

  return (
    <>
      <HeaderSection toolId="webpToJpeg" />
      <div className={`max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-800 border-gray-700 shadow-gray-900/10' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Header Section */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFileImage} className="mr-3" />
              WebP to JPEG Converter
            </h1>
            <p className="opacity-90">
              Convert WebP images to high-quality JPEG format
            </p>
          </div>

          {/* Upload Area */}
          <section 
            ref={uploadAreaRef}
            className={`border-2 border-dashed rounded-xl py-10 px-4 md:px-20 m-6 text-center cursor-pointer transition-all duration-300 ${
              isDragging 
                ? 'border-green-500 bg-green-100 dark:bg-green-900/30' 
                : images.length > 0
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-300 hover:border-green-400 hover:bg-green-50 dark:hover:bg-gray-700'
            }`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon 
              icon={faCloudUploadAlt} 
              className={`text-4xl md:text-5xl mb-4 p-6 transition-transform duration-300 hover:scale-110 ${
                darkMode ? 'text-green-400' : 'text-green-500'
              }`} 
            />
            <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
              Drag & drop your WebP files here or click to browse
            </p>
            <input 
              type="file" 
              ref={fileInputRef}
              className="hidden" 
              accept="image/webp,.webp"
              onChange={handleInputChange}
              multiple
            />
            {images.length > 0 && (
              <div className="mt-4 text-sm font-medium flex items-center justify-center gap-2">
                <span className={darkMode ? 'text-gray-200' : 'text-green-600'}>
                  {images.length} file{images.length !== 1 ? 's' : ''} selected
                </span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className={`transition-colors duration-300 ${
                    darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </section>

          {/* Quality Slider */}
          {images.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-6">
              <div>
                <label
                  htmlFor="quality"
                  className={`block text-sm font-medium mb-1 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}
                >
                  JPEG Quality ({quality}%)
                </label>
                <input
                  type="range"
                  id="quality"
                  min="10"
                  max="100"
                  value={quality}
                  onChange={(e) => setQuality(parseInt(e.target.value))}
                  className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                    darkMode ? 'bg-gray-600' : 'bg-gray-200'
                  }`}
                />
                <div className={`flex justify-between text-xs mt-1 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <span>Low</span>
                  <span>High</span>
                </div>
              </div>
              <div className="flex items-end space-x-2">
                <button
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md font-medium transition-all duration-300 ${
                    isConverting
                      ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  }`}
                  disabled={isConverting}
                  onClick={handleConvert}
                >
                  {isConverting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      Converting...
                    </>
                  ) : (
                    'Convert to JPEG'
                  )}
                </button>
                <button
                  onClick={clearAll}
                  className={`py-2 px-3 rounded-md font-medium transition-all duration-300 hover:-translate-y-0.5 ${
                    darkMode
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Image Grid */}
          {images.length > 0 && (
            <div className="m-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-semibold ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  {images.some(img => img.converted) ? 'Converted JPEGs' : 'Uploaded WebP Images'} 
                  ({images.length})
                </h2>
                {images.some(img => img.converted) && (
                  <button
                    onClick={handleDownloadAll}
                    className={`flex items-center gap-2 py-1 px-3 rounded-md font-medium transition-all duration-300 hover:-translate-y-0.5 ${
                      darkMode
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Download All
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg overflow-hidden shadow-sm transition-colors duration-300 ${
                      darkMode 
                        ? 'border-gray-700 bg-gray-700 shadow-gray-900/10' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <button
                      onClick={() => removeImage(index)}
                      className={`absolute top-2 right-2 rounded-full p-1 transition-colors duration-300 ${
                        darkMode 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                      title="Remove image"
                    >
                      <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                    </button>
                    <div className={`p-2 flex justify-center ${
                      darkMode ? 'bg-gray-600' : 'bg-gray-100'
                    }`}>
                      <img
                        src={image.convertedUrl || image.preview}
                        alt={`Preview ${index + 1}`}
                        className="max-w-full h-auto border object-contain"
                        style={{ maxHeight: "200px" }}
                        onLoad={() => URL.revokeObjectURL(image.preview)}
                      />
                    </div>
                    <div className={`p-2 border-t ${
                      darkMode 
                        ? 'bg-gray-800 border-gray-700' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <span
                          className={`text-xs truncate ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}
                          title={image.file.name}
                        >
                          {image.file.name}
                        </span>
                        {image.converted && (
                          <button
                            onClick={() => downloadImage(image.convertedUrl, `${image.name}.jpeg`)}
                            className={`text-xs py-1 px-2 rounded transition-all duration-300 hover:-translate-y-0.5 ${
                              darkMode
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>
                        )}
                      </div>
                      {image.converted && (
                        <div className={`mt-1 text-xs ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          JPEG @ {quality}% quality
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <BottomSection toolId="webpToJpeg" />
    </>
  );
}